import { useState, useEffect } from 'react';
import { Plus, Trash2, Calendar, Loader2, Pencil, Check, X, Copy, CheckCircle2, XCircle } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, fieldStyle, selectStyle, navyBtn, outlineBtn,
} from '../../styles/tokens';
import client from '../../api/client';

export default function HolidayManagement() {
  const currentYear = new Date().getFullYear();
  const [year, setYear]           = useState(currentYear);
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [newDate, setNewDate]     = useState('');
  const [newName, setNewName]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Inline edit state
  const [editId, setEditId]       = useState(null);
  const [editDate, setEditDate]   = useState('');
  const [editName, setEditName]   = useState('');

  // Copy state
  const [copyTarget, setCopyTarget] = useState('');
  const [copying, setCopying]       = useState(false);

  const yearOptions = [];
  for (let y = currentYear; y <= currentYear + 3; y++) {
    yearOptions.push(y);
  }

  useEffect(() => { fetchItems(); }, [year]);

  async function fetchItems() {
    setLoading(true);
    try {
      const res = await client.get(`/api/admin/holidays?year=${year}`);
      setItems(res.data);
    } catch (err) {
      console.error('Failed to load holidays:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!newDate || !newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await client.post('/api/admin/holidays', { date: newDate, name: newName.trim() });
      setNewDate('');
      setNewName('');
      setShowAdd(false);
      await fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear feriado');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item) {
    const dateOnly = typeof item.date === 'string' ? item.date.slice(0, 10) : item.date;
    setEditId(item.id);
    setEditDate(dateOnly);
    setEditName(item.name);
  }

  async function handleSaveEdit() {
    if (!editDate || !editName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await client.patch(`/api/admin/holidays/${editId}`, { date: editDate, name: editName.trim() });
      setEditId(null);
      await fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar feriado');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(id, isActive) {
    try {
      await client.patch(`/api/admin/holidays/${id}/toggle`, { is_active: isActive });
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cambiar estado');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar este feriado?')) return;
    try {
      await client.delete(`/api/admin/holidays/${id}`);
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar feriado');
    }
  }

  async function handleCopy() {
    if (!copyTarget) return;
    setCopying(true);
    setError('');
    try {
      const res = await client.post('/api/admin/holidays/copy', { fromYear: year, toYear: Number(copyTarget) });
      alert(res.data.message);
      setCopyTarget('');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al copiar feriados');
    } finally {
      setCopying(false);
    }
  }

  function formatDate(dateStr) {
    const dateOnly = typeof dateStr === 'string' ? dateStr.slice(0, 10) : dateStr;
    const d = new Date(dateOnly + 'T12:00:00');
    return d.toLocaleDateString('es-PR', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando feriados…
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Calendar size={20} style={{ color: AMBER }} />
          <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>
            Feriados
            <span style={{ fontSize: 13, fontWeight: '400', color: '#94a3b8', marginLeft: 8 }}>
              ({items.filter(i => i.is_active !== false).length} activos de {items.length})
            </span>
          </h2>
        </div>
        <button onClick={() => { setShowAdd(true); setError(''); setNewDate(''); setNewName(''); }} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Agregar Feriado
        </button>
      </div>

      {/* Year selector + Copy */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: '600', color: '#475569' }}>Año:</label>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          style={{ ...selectStyle, width: 'auto' }}
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <Copy size={14} style={{ color: '#64748b' }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>Copiar a:</span>
          <select
            value={copyTarget}
            onChange={e => setCopyTarget(e.target.value)}
            style={{ ...selectStyle, width: 'auto', fontSize: 12 }}
          >
            <option value="">-- Año --</option>
            {yearOptions.filter(y => y !== year).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={handleCopy}
            disabled={!copyTarget || copying}
            style={{ ...outlineBtn, fontSize: 12, padding: '4px 10px', opacity: (!copyTarget || copying) ? 0.5 : 1 }}
          >
            {copying ? 'Copiando…' : 'Copiar'}
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ ...bubbleStyle, marginBottom: 20, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 180px' }}>
              <label style={labelStyle}>Fecha</label>
              <input
                type="date"
                value={newDate}
                onChange={e => setNewDate(e.target.value)}
                style={fieldStyle}
                autoFocus
              />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Nombre del Feriado</label>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                style={fieldStyle}
                placeholder="ej., Día de la Independencia"
              />
            </div>
            <button onClick={handleAdd} disabled={saving} style={{ ...navyBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Agregando…' : 'Agregar'}
            </button>
            <button onClick={() => setShowAdd(false)} style={outlineBtn}>Cancelar</button>
          </div>
          {error && (
            <div style={{
              marginTop: 8, padding: '6px 12px', background: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca', borderRadius: 6, fontSize: 12,
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Error outside add form */}
      {error && !showAdd && (
        <div style={{
          marginBottom: 12, padding: '6px 12px', background: '#fef2f2', color: '#991b1b',
          border: '1px solid #fecaca', borderRadius: 6, fontSize: 12,
        }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={bubbleStyle}>
        {/* Table header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 0 10px 0', borderBottom: `2px solid ${BORDER_COLOR}`,
          fontSize: 12, fontWeight: '700', color: '#64748b', textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          <span style={{ flex: '0 0 200px' }}>Fecha</span>
          <span style={{ flex: 1 }}>Nombre del Feriado</span>
          <span style={{ width: 120, textAlign: 'center' }}></span>
        </div>

        {/* Rows */}
        {items.map(item => (
          <div key={item.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
          }}>
            {editId === item.id ? (
              <>
                <div style={{ flex: '0 0 200px', paddingRight: 8 }}>
                  <input
                    type="date"
                    value={editDate}
                    onChange={e => setEditDate(e.target.value)}
                    style={{ ...fieldStyle, fontSize: 13, padding: '4px 8px' }}
                  />
                </div>
                <div style={{ flex: 1, paddingRight: 8 }}>
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveEdit()}
                    style={{ ...fieldStyle, fontSize: 13, padding: '4px 8px' }}
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: 4, width: 120, justifyContent: 'center' }}>
                  <button onClick={handleSaveEdit} disabled={saving} style={iconBtn} title="Guardar">
                    <Check size={13} style={{ color: '#16a34a' }} />
                  </button>
                  <button onClick={() => setEditId(null)} style={iconBtn} title="Cancelar">
                    <X size={13} style={{ color: '#64748b' }} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <span style={{ flex: '0 0 200px', fontSize: 13, color: '#475569', opacity: item.is_active === false ? 0.5 : 1 }}>
                  {formatDate(item.date)}
                </span>
                <span style={{
                  flex: 1, fontSize: 14, fontWeight: '500', color: NAVY,
                  opacity: item.is_active === false ? 0.5 : 1,
                  textDecoration: item.is_active === false ? 'line-through' : 'none',
                }}>
                  {item.name}
                </span>
                <div style={{ display: 'flex', gap: 4, width: 120, justifyContent: 'center' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleActive(item.id, !item.is_active); }}
                    style={{
                      ...iconBtn,
                      color: item.is_active ? '#16a34a' : '#94a3b8',
                    }}
                    title={item.is_active ? 'Desactivar' : 'Activar'}
                  >
                    {item.is_active ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                  </button>
                  <button onClick={() => startEdit(item)} style={iconBtn} title="Editar">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} style={iconBtn} title="Eliminar">
                    <Trash2 size={13} style={{ color: '#dc2626' }} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {items.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No hay feriados registrados para {year}
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 4,
};

const iconBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
